export interface ILegendSeriesIdentity {
    index: number;
    userOptions: {
        id?: string;
    };
}

export interface ILegendSeriesVisibility {
    name: string;
    visible: boolean;
}

const LEGEND_DOUBLE_CLICK_DELAY_IN_MS = 300;

export function isSameLegendSeries (
    pending: ILegendSeriesIdentity | null,
    clicked: ILegendSeriesIdentity,
): boolean {
    if (!pending) return false;

    const pendingId = pending.userOptions?.id;
    const clickedId = clicked.userOptions?.id;
    if (pendingId != null && clickedId != null) {
        return pendingId === clickedId;
    }

    return pending.index === clicked.index;
}

export function resolveIsolateVisibility (
    seriesList: Array<ILegendSeriesVisibility>,
    targetName: string,
): Array<boolean> {
    const allVisible = seriesList.every(series => series.visible || series.name === targetName);

    if (allVisible) {
        return seriesList.map(series => series.name === targetName);
    }

    return seriesList.map(() => true);
}

export interface ILegendItemClickHandlerOptions<TSeries extends ILegendSeriesIdentity> {
    delayMs?: number;
    onIsolate: (series: TSeries) => void;
    onSingleClickSync: (series: TSeries) => void;
}

export interface ILegendItemClickHandler<TSeries extends ILegendSeriesIdentity> {
    handleClick: (series: TSeries) => boolean;
    clear: () => void;
}

export class LegendItemClickHandler<TSeries extends ILegendSeriesIdentity> implements ILegendItemClickHandler<TSeries> {
    private legendClickTimer: ReturnType<typeof setTimeout> | null = null;

    private pendingLegendClickSeries: TSeries | null = null;

    private readonly onIsolate: (series: TSeries) => void;

    private readonly onSingleClickSync: (series: TSeries) => void;

    constructor ({ onIsolate, onSingleClickSync }: ILegendItemClickHandlerOptions<TSeries>) {
        this.onIsolate = onIsolate;
        this.onSingleClickSync = onSingleClickSync;
    }

    clear (): void {
        if (this.legendClickTimer) {
            clearTimeout(this.legendClickTimer);
            this.legendClickTimer = null;
        }
        this.pendingLegendClickSeries = null;
    }

    handleClick (series: TSeries): boolean {
        if (this.legendClickTimer) {
            clearTimeout(this.legendClickTimer);
            this.legendClickTimer = null;

            if (isSameLegendSeries(this.pendingLegendClickSeries, series)) {
                this.pendingLegendClickSeries = null;
                this.onIsolate(series);
                return false;
            }

            if (this.pendingLegendClickSeries) {
                this.onSingleClickSync(this.pendingLegendClickSeries);
            }
            this.pendingLegendClickSeries = null;
        }

        this.pendingLegendClickSeries = series;
        this.legendClickTimer = setTimeout(() => {
            this.legendClickTimer = null;
            this.pendingLegendClickSeries = null;
            this.onSingleClickSync(series);
        }, LEGEND_DOUBLE_CLICK_DELAY_IN_MS);

        return true;
    }
}
